/**
 * PDF History Import Dialog
 * Importa ASO/exame ou treinamento de histórico a partir de um ou mais PDFs (um documento por
 * registro) — a IA extrai os dados de cada arquivo, sem precisar de planilha. Componente genérico,
 * usado tanto pelo fluxo de ASO/Exames quanto pelo de Treinamentos (cada chamador passa seu
 * próprio `onImport`).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { companyService } from '../../../services/companyService';

export interface PdfHistoryImportResult {
  fileName: string;
  success: boolean;
  employeeName?: string;
  error?: string;
}

interface PdfHistoryImportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  onImport: (
    files: File[],
    options: { companyId?: number; autoCreateCatalogEntries?: boolean }
  ) => Promise<PdfHistoryImportResult[]>;
  /** Disparado quando pelo menos um arquivo importou com sucesso — para atualizar a lista de logs. */
  onImportComplete?: () => void;
}

const PdfHistoryImportDialog: React.FC<PdfHistoryImportDialogProps> = ({
  open,
  onClose,
  title,
  description,
  onImport,
  onImportComplete,
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [autoCreateCatalogEntries, setAutoCreateCatalogEntries] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PdfHistoryImportResult[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      companyService.getCompaniesForDropdown().then(setCompanies).catch(() => {});
    }
  }, [open]);

  const handleFilesSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (selected.length === 0) return;
    setFiles(prev => [...prev, ...selected]);
    setResults(null);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleImport = useCallback(async () => {
    if (files.length === 0) return;
    setLoading(true);
    setResults(null);
    try {
      const options: { companyId?: number; autoCreateCatalogEntries?: boolean } = {};
      if (selectedCompanyId) options.companyId = Number(selectedCompanyId);
      if (autoCreateCatalogEntries) options.autoCreateCatalogEntries = true;
      const imported = await onImport(files, options);
      setResults(imported);
      if (imported.some(r => r.success)) onImportComplete?.();
    } catch {
      setResults(files.map(f => ({ fileName: f.name, success: false, error: 'Erro ao importar' })));
    } finally {
      setLoading(false);
    }
  }, [files, selectedCompanyId, autoCreateCatalogEntries, onImport, onImportComplete]);

  const handleClose = useCallback(() => {
    setSelectedCompanyId('');
    setAutoCreateCatalogEntries(false);
    setFiles([]);
    setResults(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {companies.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value as number)}
              label="Empresa"
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={autoCreateCatalogEntries}
              onChange={(e) => setAutoCreateCatalogEntries(e.target.checked)}
            />
          }
          label="Cadastrar automaticamente itens não encontrados no catálogo"
          sx={{ mb: 2, display: 'block' }}
        />

        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} fullWidth sx={{ mb: 2 }}>
          Selecionar PDFs
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            hidden
            onChange={handleFilesSelected}
          />
        </Button>

        {files.length > 0 && (
          <List dense sx={{ maxHeight: 260, overflow: 'auto', mb: 1 }}>
            {files.map((file, index) => {
              const result = results?.find(r => r.fileName === file.name);
              return (
                <ListItem
                  key={`${file.name}-${index}`}
                  secondaryAction={
                    result ? (
                      result.success ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <Tooltip title={result.error ?? 'Erro ao importar'}>
                          <ErrorIcon color="error" fontSize="small" />
                        </Tooltip>
                      )
                    ) : (
                      !loading && (
                        <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    )
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}><PdfIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={result ? (result.success ? result.employeeName : result.error) : undefined}
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {loading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Processando com IA — arquivos maiores em quantidade podem levar alguns minutos.
            </Typography>
          </Box>
        )}

        {results && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {results.filter(r => r.success).length} de {results.length} arquivo(s) importado(s) com sucesso.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{results ? 'Fechar' : 'Cancelar'}</Button>
        {!results && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={files.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? 'Importando...' : 'Importar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PdfHistoryImportDialog;
